import React, { useCallback, useEffect, useState } from 'react';
import { invoicesAPI, contractsAPI } from './api';

const PAYMENT_COLORS = { paid: 'pay-paid', partial: 'pay-partial', unpaid: 'pay-unpaid' };
const PAYMENT_LABELS = { paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid' };

const amount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const fmt = (value, currency = 'INR') => {
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount(value));
  } catch {
    return `${currency || 'INR'} ${amount(value).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
};

const fmtShort = (value) => {
  const v = Math.abs(amount(value));
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const fmtDate = (value, fallback = 'Ongoing') =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : fallback;

const toDateInputValue = (value) => (value ? String(value).split('T')[0] : '');

const parseInvoiceItems = (items) => {
  if (Array.isArray(items)) {
    return items.map((item, index) => ({
      id: item?.id ?? index + 1,
      description: item?.description || '',
      amount: item?.amount ?? '',
    }));
  }
  if (!items) return [];

  try {
    const parsed = JSON.parse(items);
    return Array.isArray(parsed)
      ? parsed.map((item, index) => ({
          id: item?.id ?? index + 1,
          description: item?.description || '',
          amount: item?.amount ?? '',
        }))
      : [];
  } catch {
    return [];
  }
};

const normalizeInvoice = (record) => ({
  id:             record.id,
  invoiceNumber:  record.invoiceNumber || record.invoice_number || '',
  invoiceDate:    toDateInputValue(record.invoiceDate || record.invoice_date),
  clientName:     record.clientName || record.client_name || '',
  total:          amount(record.total),
  gstAmount:      amount(record.gstAmount || record.gst_amount),
  subtotal:       amount(record.subtotal),
  paymentStatus:  record.paymentStatus || record.payment_status || 'unpaid',
  paymentDate:    record.paymentDate || record.payment_date || '',
  paymentAmount:  amount(record.paymentAmount || record.payment_amount),
  paymentMode:    record.paymentMode || record.payment_mode || '',
  paymentNotes:   record.paymentNotes || record.payment_notes || '',
  tdsAmount:      amount(record.tdsAmount || record.tds_amount),
  tdsRate:        amount(record.tdsRate || record.tds_rate),
  currency:       record.currency || 'INR',
  contractId:     record.contractId || record.contract_id || null,
  agreementNumber:record.agreementNumber || record.agreement_number || '',
  placeOfSupply:  record.placeOfSupply || record.place_of_supply || '',
  items:          parseInvoiceItems(record.items),
  prefix:         record.prefix || '',
});

const isDomesticInvoice = (invoice) => (invoice.currency || 'INR') === 'INR';
const roundMoney = (value) => Math.round(amount(value) * 100) / 100;
const deriveTdsRate = (tdsAmount, subtotal) => {
  const base = amount(subtotal);
  if (base <= 0 || amount(tdsAmount) <= 0) return 0;
  return (amount(tdsAmount) / base) * 100;
};

const daysUntil = (value) => {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

const currentValueFor = (contract) => amount(contract.current_contract_value || contract.contract_value);
const invoicedAmountFor = (contract) => amount(contract.invoiced_amount);
const remainingValueFor = (contract) => currentValueFor(contract) - invoicedAmountFor(contract);
const billedPctFor = (contract) => {
  const currentValue = currentValueFor(contract);
  if (currentValue <= 0) return 0;
  return Math.min((invoicedAmountFor(contract) / currentValue) * 100, 100);
};

const isExpiringSoon = (contract, withinDays = 45) => {
  if (contract.status !== 'active') return false;
  const days = daysUntil(contract.revised_end_date || contract.end_date);
  return days !== null && days >= 0 && days <= withinDays;
};

const attentionFlagsFor = (contract) => {
  const flags = [];
  const remaining = remainingValueFor(contract);
  if (contract.status === 'draft') flags.push('Draft contract');
  if (contract.status === 'active' && !Number(contract.milestone_count)) flags.push('Add milestones');
  if (contract.status === 'active' && !Number(contract.deliverable_count)) flags.push('Add deliverables');
  if (contract.status === 'active' && !Number(contract.document_count)) flags.push('Add documents');
  if (contract.status === 'active' && remaining < 0) flags.push('Over ceiling');
  if (isExpiringSoon(contract)) flags.push('Renew soon');
  return flags;
};

function PaymentModal({ invoice, onClose, onSaved }) {
  const domesticTdsEnabled = isDomesticInvoice(invoice);
  const [form, setForm] = useState({
    payment_status: invoice.paymentStatus || 'paid',
    payment_date: invoice.paymentDate || new Date().toISOString().split('T')[0],
    payment_amount: invoice.paymentStatus === 'unpaid'
      ? ''
      : (invoice.paymentAmount || invoice.paymentAmount === 0 ? String(invoice.paymentAmount) : String(invoice.total || '')),
    payment_mode: invoice.paymentMode || 'bank_transfer',
    payment_notes: invoice.paymentNotes || '',
    tds_method: domesticTdsEnabled && invoice.tdsRate > 0 ? 'percentage' : 'receipt',
    tds_rate: invoice.tdsRate ? String(invoice.tdsRate) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setPaymentStatus = (value) => {
    setForm(prev => {
      if (value !== 'unpaid') return { ...prev, payment_status: value };
      return {
        ...prev,
        payment_status: 'unpaid',
        payment_amount: '',
      };
    });
  };
  const invoiceTotal = roundMoney(invoice.total);
  const manualBankReceived = roundMoney(form.payment_amount);
  const inputTdsRate = Math.max(amount(form.tds_rate), 0);
  const hasReceiptInputs = manualBankReceived > 0 || (form.payment_status !== 'unpaid' && inputTdsRate > 0);
  const canAutoTdsFromReceipt = domesticTdsEnabled && manualBankReceived > 0;
  const tdsAmount = (() => {
    if (!domesticTdsEnabled) return 0;
    if (!hasReceiptInputs && form.payment_status === 'unpaid') return 0;
    if (form.tds_method === 'percentage') {
      return roundMoney((amount(invoice.subtotal) * inputTdsRate) / 100);
    }
    if (canAutoTdsFromReceipt) {
      return roundMoney(Math.max(invoiceTotal - manualBankReceived, 0));
    }
    return 0;
  })();
  const bankReceived = (() => {
    if (form.payment_amount !== '') return manualBankReceived;
    if (domesticTdsEnabled && form.tds_method === 'percentage' && form.payment_status === 'paid') {
      return roundMoney(Math.max(invoiceTotal - tdsAmount, 0));
    }
    if (form.payment_status === 'unpaid') return 0;
    return 0;
  })();
  const settledAmount = roundMoney(bankReceived + tdsAmount);
  const balanceAmount = roundMoney(Math.max(invoiceTotal - settledAmount, 0));
  const derivedTdsRate = form.tds_method === 'percentage' ? inputTdsRate : deriveTdsRate(tdsAmount, invoice.subtotal);
  const effectivePaymentStatus = (() => {
    if (settledAmount <= 0) return 'unpaid';
    if (domesticTdsEnabled && form.tds_method === 'receipt' && manualBankReceived > 0) return 'paid';
    return balanceAmount <= 0 ? 'paid' : 'partial';
  })();
  const statusMismatch = effectivePaymentStatus !== form.payment_status;
  const autoReceiptModeNote = 'Auto mode derives TDS as Invoice Total minus Bank Amount Received, so it assumes a full settlement.';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        payment_status: effectivePaymentStatus,
        payment_amount: effectivePaymentStatus === 'unpaid' ? 0 : bankReceived.toFixed(2),
        tds_amount: domesticTdsEnabled ? tdsAmount.toFixed(2) : 0,
      };
      const updated = await invoicesAPI.setPayment(invoice.id, payload);
      onSaved(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box payment-modal-box">
        <div className="modal-header">
          <h3 className="modal-title">Record Payment</h3>
          <div className="modal-inv-ref">{invoice.invoiceNumber} · {fmt(invoice.total, invoice.currency)}</div>
        </div>
        <form onSubmit={submit} className="modal-body payment-modal-body">
          <div className="payment-modal-layout">
            <div className="payment-modal-main">
              <div className="modal-grid3">
                <div className="modal-fg">
                  <label className="modal-lbl">Payment Status</label>
                  <select className="modal-inp" value={form.payment_status} onChange={(e) => setPaymentStatus(e.target.value)}>
                    <option value="paid">Paid in Full</option>
                    <option value="partial">Partial Payment</option>
                    <option value="unpaid">Mark as Unpaid</option>
                  </select>
                </div>
                <div className="modal-fg">
                  <label className="modal-lbl">Payment Date</label>
                  <input className="modal-inp" type="date" value={form.payment_date} onChange={(e) => set('payment_date', e.target.value)} />
                </div>
                <div className="modal-fg">
                  <label className="modal-lbl">Mode</label>
                  <select className="modal-inp" value={form.payment_mode} onChange={(e) => set('payment_mode', e.target.value)}>
                    <option value="bank_transfer">Bank Transfer / NEFT</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                    <option value="wire">International Wire</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="modal-grid3">
                <div className="modal-fg">
                  <label className="modal-lbl">Bank Amount Received</label>
                  <input
                    className="modal-inp"
                    type="number"
                    value={form.payment_amount}
                    step="0.01"
                    min="0"
                    onChange={(e) => set('payment_amount', e.target.value)}
                    placeholder="Actual bank credit"
                  />
                </div>

                {domesticTdsEnabled ? (
                  <>
                    <div className="modal-fg">
                      <label className="modal-lbl">TDS Method</label>
                      <div className="modal-method-toggle">
                        <button
                          type="button"
                          className={form.tds_method === 'receipt' ? 'active' : ''}
                          onClick={() => set('tds_method', 'receipt')}
                        >
                          Auto from Bank Received
                        </button>
                        <button
                          type="button"
                          className={form.tds_method === 'percentage' ? 'active' : ''}
                          onClick={() => set('tds_method', 'percentage')}
                        >
                          Percentage
                        </button>
                      </div>
                    </div>

                    <div className="modal-fg">
                      <label className="modal-lbl">
                        {form.tds_method === 'percentage' ? 'TDS %' : 'Computed TDS Amount'}
                      </label>
                      {form.tds_method === 'percentage' ? (
                        <input
                          className="modal-inp"
                          type="number"
                          value={form.tds_rate}
                          step="0.01"
                          min="0"
                          onChange={(e) => set('tds_rate', e.target.value)}
                          placeholder="Example: 10"
                        />
                      ) : (
                        <input
                          className="modal-inp"
                          type="text"
                          value={fmt(tdsAmount)}
                          readOnly
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <div className="modal-fg modal-grid-span-2">
                    <label className="modal-lbl">TDS</label>
                    <input className="modal-inp" type="text" value="Available only for INR / domestic receipts" readOnly />
                  </div>
                )}
              </div>

              {domesticTdsEnabled && form.tds_method === 'percentage' && (
                <div className="modal-grid2">
                  <div className="modal-fg">
                    <label className="modal-lbl">Computed TDS Amount</label>
                    <input className="modal-inp" type="text" value={fmt(tdsAmount)} readOnly />
                  </div>
                  <div className="modal-fg">
                    <label className="modal-lbl">Derived TDS %</label>
                    <input className="modal-inp" type="text" value={derivedTdsRate > 0 ? `${derivedTdsRate.toFixed(2)}%` : '—'} readOnly />
                  </div>
                </div>
              )}

              <div className="modal-fg">
                <label className="modal-lbl">Payment Notes</label>
                <textarea
                  className="modal-inp modal-inp-textarea"
                  rows="4"
                  value={form.payment_notes}
                  onChange={(e) => set('payment_notes', e.target.value)}
                  placeholder="UTR / cheque no. / TDS note / certificate reference"
                />
              </div>
            </div>

            <div className="payment-modal-side">
              <div className="modal-payment-summary">
                <div className="modal-payment-chip">
                  <span>Invoice Total</span>
                  <strong>{fmt(invoice.total, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Bank Received</span>
                  <strong>{fmt(bankReceived, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>TDS Deducted</span>
                  <strong>{fmt(tdsAmount, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Settled Against Invoice</span>
                  <strong>{fmt(settledAmount, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Outstanding</span>
                  <strong>{fmt(balanceAmount, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Effective Status</span>
                  <strong>{effectivePaymentStatus === 'paid' ? 'Paid' : effectivePaymentStatus === 'partial' ? 'Partial' : 'Unpaid'}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Derived TDS %</span>
                  <strong>{derivedTdsRate > 0 ? `${derivedTdsRate.toFixed(2)}%` : '—'}</strong>
                </div>
              </div>

              <div className="modal-help">
                {domesticTdsEnabled
                  ? (form.tds_method === 'percentage'
                    ? 'Percentage mode computes TDS on the invoice taxable value and keeps bank receipt as the actual cash received.'
                    : autoReceiptModeNote)
                  : 'TDS deduction is currently enabled only for INR / domestic invoice receipts.'}
              </div>
              {statusMismatch && (
                <div className="modal-help">
                  Based on the values entered, this payment will be saved as <strong>{effectivePaymentStatus}</strong>.
                </div>
              )}
            </div>
          </div>
          {error && <div className="modal-error">{error}</div>}
          <div className="modal-footer">
            <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-save" disabled={saving}>{saving ? 'Saving...' : 'Save Payment'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Dashboard({ onNewInvoice, onLoadInvoice, onOpenContracts }) {
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payModal, setPayModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invoiceRows, contractRows] = await Promise.all([
        invoicesAPI.list(),
        contractsAPI.list(),
      ]);
      setInvoices(invoiceRows.map(normalizeInvoice));
      setContracts(contractRows);
    } catch {
      setInvoices([]);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeContracts = contracts.filter(contract => contract.status === 'active');
  const draftContracts = contracts.filter(contract => contract.status === 'draft');
  const expiringSoonContracts = activeContracts.filter(contract => isExpiringSoon(contract));
  const mixedCurrencies = new Set(contracts.map(contract => contract.billing_currency || 'INR')).size > 1;

  const totalPortfolio = contracts.reduce((sum, contract) => sum + currentValueFor(contract), 0);
  const totalInvoiced = contracts.reduce((sum, contract) => sum + invoicedAmountFor(contract), 0);
  const totalRemaining = contracts.reduce((sum, contract) => sum + remainingValueFor(contract), 0);
  const setupNeededContracts = activeContracts.filter(contract =>
    !Number(contract.milestone_count) || !Number(contract.deliverable_count) || !Number(contract.document_count)
  );

  const gapRows = [
    {
      label: 'Missing milestones',
      value: activeContracts.filter(contract => !Number(contract.milestone_count)).length,
      hint: 'Active contracts with no billing milestones mapped yet.',
    },
    {
      label: 'Missing deliverables',
      value: activeContracts.filter(contract => !Number(contract.deliverable_count)).length,
      hint: 'Execution outputs are not yet linked to an invoice plan.',
    },
    {
      label: 'Missing documents',
      value: activeContracts.filter(contract => !Number(contract.document_count)).length,
      hint: 'Audit and donor records still need to be registered.',
    },
    {
      label: 'Draft contracts',
      value: draftContracts.length,
      hint: 'Contracts still waiting for final data before going live.',
    },
  ];

  const portfolioContracts = [...activeContracts]
    .map(contract => ({
      ...contract,
      currentValue: currentValueFor(contract),
      invoicedValue: invoicedAmountFor(contract),
      remainingValue: remainingValueFor(contract),
      billedPct: billedPctFor(contract),
    }))
    .sort((a, b) => b.currentValue - a.currentValue)
    .slice(0, 6);

  const attentionContracts = contracts
    .map(contract => {
      const flags = attentionFlagsFor(contract);
      const remainingValue = remainingValueFor(contract);
      return {
        ...contract,
        flags,
        remainingValue,
        currentValue: currentValueFor(contract),
        invoicedValue: invoicedAmountFor(contract),
        billedPct: billedPctFor(contract),
        attentionScore:
          (flags.includes('Over ceiling') ? 50 : 0) +
          (flags.includes('Renew soon') ? 30 : 0) +
          (flags.includes('Draft contract') ? 10 : 0) +
          flags.length,
      };
    })
    .filter(contract => contract.flags.length > 0)
    .sort((a, b) => {
      if (b.attentionScore !== a.attentionScore) return b.attentionScore - a.attentionScore;
      return (daysUntil(a.revised_end_date || a.end_date) ?? 9999) - (daysUntil(b.revised_end_date || b.end_date) ?? 9999);
    })
    .slice(0, 8);

  const renewalContracts = [...activeContracts]
    .filter(contract => contract.revised_end_date || contract.end_date)
    .sort((a, b) => (daysUntil(a.revised_end_date || a.end_date) ?? 9999) - (daysUntil(b.revised_end_date || b.end_date) ?? 9999))
    .slice(0, 5);

  const handlePaymentSaved = (updated) => {
    setInvoices(prev => prev.map(invoice => invoice.id === updated.id ? normalizeInvoice(updated) : invoice));
    setPayModal(null);
    load();
  };

  if (loading) return <div className="db-loading">Loading dashboard...</div>;

  return (
    <div className="db-page">
      <div className="db-hero">
        <div className="db-hero-copy">
          <div className="db-hero-title">Contract Portfolio</div>
          <div className="db-hero-sub">
            Track contract value, setup readiness, renewals, and billing progress from one place.
          </div>
        </div>
        <div className="db-hero-actions">
          <button className="db-hero-btn ghost" onClick={() => onOpenContracts?.()}>Open Contracts</button>
          <button className="db-hero-btn solid" onClick={() => onNewInvoice(portfolioContracts[0] || null)}>+ New Invoice</button>
        </div>
      </div>

      <div className="db-kpi-row db-kpi-row-compact">
        {[
          {
            cls: 'db-kpi-activecontracts',
            label: 'Active Contracts',
            value: String(activeContracts.length),
            sub: `${draftContracts.length} draft · ${contracts.length} total`,
          },
          {
            cls: 'db-kpi-portfolio',
            label: 'Portfolio Value',
            value: fmtShort(totalPortfolio),
            sub: mixedCurrencies ? 'Mixed contract currencies' : 'Current contract ceiling',
          },
          {
            cls: 'db-kpi-invoiced',
            label: 'Invoiced',
            value: fmtShort(totalInvoiced),
            sub: mixedCurrencies ? 'Mixed invoice currencies' : 'Across all linked contracts',
          },
          {
            cls: totalRemaining < 0 ? 'db-kpi-overrun' : 'db-kpi-unbilled',
            label: totalRemaining < 0 ? 'Overrun' : 'Remaining Value',
            value: fmtShort(totalRemaining),
            sub: totalRemaining < 0 ? 'Invoices above current ceiling' : 'Still available to bill',
          },
          {
            cls: setupNeededContracts.length > 0 ? 'db-kpi-setup' : 'db-kpi-ready',
            label: 'Needs Setup',
            value: String(setupNeededContracts.length),
            sub: `${expiringSoonContracts.length} expiring in 45 days`,
          },
        ].map(card => (
          <div key={card.label} className={`db-kpi ${card.cls}`}>
            <div className="db-kpi-icon-wrap">
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
                {card.label === 'Active Contracts' && (
                  <>
                    <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                    <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </>
                )}
                {card.label === 'Portfolio Value' && (
                  <>
                    <path d="M3 7h18v10H3z" />
                    <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
                    <path d="M3 12h18" />
                  </>
                )}
                {card.label === 'Invoiced' && (
                  <>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </>
                )}
                {(card.label === 'Remaining Value' || card.label === 'Overrun') && (
                  <>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    {card.label === 'Overrun'
                      ? <polyline points="5 12 12 5 19 12" />
                      : <polyline points="19 12 12 19 5 12" />}
                  </>
                )}
                {card.label === 'Needs Setup' && (
                  <>
                    <circle cx="12" cy="12" r="9" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </>
                )}
              </svg>
            </div>
            <div>
              <div className="db-kpi-label">{card.label}</div>
              <div className="db-kpi-value">{card.value}</div>
              <div className="db-kpi-sub">{card.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="db-overview-grid">
        <div className="db-section">
          <div className="db-section-head">
            <span>Portfolio Overview</span>
            <button className="db-section-action" onClick={() => onOpenContracts?.()}>View All</button>
          </div>
          {portfolioContracts.length === 0 ? (
            <div className="db-empty-sm">No active contracts yet. Add a contract to start managing billing and deliverables.</div>
          ) : (
            <div className="db-portfolio-list">
              {portfolioContracts.map(contract => (
                <div key={contract.id} className="db-portfolio-row">
                  <div className="db-portfolio-main">
                    <div className="db-portfolio-top">
                      <div>
                        <div className="db-portfolio-name">{contract.project_name}</div>
                        <div className="db-portfolio-sub">
                          {contract.client_name}
                          {contract.donor_name ? ` · ${contract.donor_name}` : ''}
                        </div>
                      </div>
                      <button className="db-portfolio-action" onClick={() => onNewInvoice(contract)}>+ Invoice</button>
                    </div>
                    <div className="db-portfolio-meta">
                      {fmtDate(contract.start_date, 'Start TBD')} -> {fmtDate(contract.revised_end_date || contract.end_date)}
                      {' · '}
                      {contract.milestone_count || 0} milestones
                      {' · '}
                      {contract.deliverable_count || 0} deliverables
                      {' · '}
                      {contract.document_count || 0} docs
                    </div>
                    <div className="db-portfolio-bar-wrap">
                      <div className="db-portfolio-bar" style={{ width: `${contract.billedPct}%` }} />
                    </div>
                  </div>
                  <div className="db-portfolio-side">
                    <strong>{fmt(contract.currentValue, contract.billing_currency || 'INR')}</strong>
                    <span>{fmt(contract.invoicedValue, contract.billing_currency || 'INR')} billed</span>
                    <span className={contract.remainingValue < 0 ? 'danger' : ''}>
                      {fmt(contract.remainingValue, contract.billing_currency || 'INR')} remaining
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="db-side-stack">
          <div className="db-indicator-card">
            <div className="db-ind-title">Execution Setup Gaps</div>
            <div className="db-gap-list">
              {gapRows.map(row => (
                <div key={row.label} className="db-gap-row">
                  <div>
                    <div className="db-gap-label">{row.label}</div>
                    <div className="db-gap-hint">{row.hint}</div>
                  </div>
                  <div className={`db-gap-value${row.value > 0 ? ' warn' : ''}`}>{row.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="db-indicator-card">
            <div className="db-ind-title">Renewals & Closures</div>
            {renewalContracts.length === 0 ? (
              <div className="db-ind-empty">No contract end dates recorded yet.</div>
            ) : (
              <div className="db-renewal-list">
                {renewalContracts.map(contract => {
                  const days = daysUntil(contract.revised_end_date || contract.end_date);
                  return (
                    <div key={contract.id} className="db-renewal-row">
                      <div>
                        <div className="db-renewal-name">{contract.project_name}</div>
                        <div className="db-renewal-sub">{contract.client_name}</div>
                      </div>
                      <div className="db-renewal-side">
                        <strong>{fmtDate(contract.revised_end_date || contract.end_date)}</strong>
                        <span className={days !== null && days <= 45 ? 'warn' : ''}>
                          {days === null ? 'No end date' : days === 0 ? 'Ends today' : `${days} days left`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="db-section">
        <div className="db-section-head">
          <span>Contracts Needing Attention</span>
          <span className="db-count">{attentionContracts.length}</span>
        </div>
        {attentionContracts.length === 0 ? (
          <div className="db-empty-sm">No contract issues detected right now. Your active portfolio is fully set up.</div>
        ) : (
          attentionContracts.map(contract => (
            <div key={contract.id} className="db-contract-attn-row">
              <div className="db-contract-attn-main">
                <div className="db-contract-attn-top">
                  <div className="db-contract-attn-name">{contract.project_name}</div>
                  <div className="db-contract-attn-flags">
                    {contract.flags.map(flag => (
                      <span key={flag} className={`db-attn-flag${flag === 'Over ceiling' ? ' danger' : flag === 'Renew soon' ? ' warning' : ''}`}>
                        {flag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="db-contract-attn-sub">
                  {contract.client_name}
                  {contract.contract_number ? ` · #${contract.contract_number}` : ''}
                  {contract.donor_name ? ` · ${contract.donor_name}` : ''}
                </div>
                <div className="db-contract-attn-meta">
                  {fmtDate(contract.revised_end_date || contract.end_date)}
                  {' · '}
                  {contract.milestone_count || 0} milestones
                  {' · '}
                  {contract.deliverable_count || 0} deliverables
                  {' · '}
                  {contract.document_count || 0} docs
                </div>
                <div className="db-portfolio-bar-wrap slim">
                  <div className="db-portfolio-bar" style={{ width: `${contract.billedPct}%` }} />
                </div>
              </div>
              <div className="db-contract-attn-side">
                <strong>{fmt(contract.currentValue, contract.billing_currency || 'INR')}</strong>
                <span>{fmt(contract.invoicedValue, contract.billing_currency || 'INR')} billed</span>
                <span className={contract.remainingValue < 0 ? 'danger' : ''}>
                  {fmt(contract.remainingValue, contract.billing_currency || 'INR')} remaining
                </span>
                <button className="db-cr-inv-btn" onClick={() => onNewInvoice(contract)}>+ Invoice</button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="db-section">
        <div className="db-section-head">
          <span>Recent Invoices</span>
          <button className="db-section-action" onClick={() => onNewInvoice(null)}>+ New Invoice</button>
        </div>
        {invoices.length === 0 ? (
          <div className="db-empty-sm">No invoices yet. Create your first invoice from a contract or click + New Invoice.</div>
        ) : (
          invoices.slice(0, 10).map(invoice => (
            <div key={invoice.id} className="db-inv-row">
              <div className="db-inv-main">
                <span className="db-inv-num">{invoice.invoiceNumber}</span>
                <span className="db-inv-client">{invoice.clientName || invoice.agreementNumber || '—'}</span>
              </div>
              <div className="db-inv-right">
                <span className="db-inv-date">{fmtDate(invoice.invoiceDate, '')}</span>
                <span className="db-inv-amt">{fmt(invoice.total, invoice.currency)}</span>
                <span className={`db-pay-badge ${PAYMENT_COLORS[invoice.paymentStatus] || 'pay-unpaid'}`}>
                  {PAYMENT_LABELS[invoice.paymentStatus] || invoice.paymentStatus}
                </span>
                <button className="db-inv-pay-btn" onClick={() => setPayModal(invoice)} title="Record payment">
                  {invoice.paymentStatus === 'paid' ? '✓' : '₹'}
                </button>
                <button className="db-inv-edit-btn" onClick={() => onLoadInvoice(invoice)} title="Open invoice">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {payModal && <PaymentModal invoice={payModal} onClose={() => setPayModal(null)} onSaved={handlePaymentSaved} />}
    </div>
  );
}
